import { useEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { hasPermission } from '@entities/access';
import { useAuth } from '@entities/auth';
import { mergeNotifications, workspaceDocumentTitle } from '@entities/admin-notification';
import { formatRbacError, isUncertainMutationError } from '@entities/withdrawal';
import { useMerchantSettings } from '@features/merchant-settings';
import { AdminCommandDialog } from '@features/withdrawal';
import { createActionLock } from '@features/workspace-withdrawal-actions/lib/action-lock';
import { ForbiddenError } from '@shared/api/errors/errors';
import type {
  AdminNotification,
  WorkspaceNotificationCategory,
  WorkspaceScope,
  WorkspaceWithdrawal,
} from '@shared/api/contracts/workspace';
import { useApiServices } from '@shared/lib/use-api-services';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  StatusBanner,
  Textarea,
  useToast,
} from '@shared/ui';
import { EmergencyGrantDialog } from '@widgets/withdrawal/admin-withdrawals-table';
import { NotificationFeed } from '@widgets/workspace-notifications';
import { WorkspaceQueue } from '@widgets/workspace-withdrawals';

const SCOPE_KEY = 'paygatex.workspace.scope';
const FILTER_KEY = 'paygatex.workspace.notifications';
const SOUND_KEY = 'paygatex.workspace.sound';

type FeedCategory = 'all' | WorkspaceNotificationCategory;

const FEED_CATEGORIES: FeedCategory[] = ['all', 'withdrawals', 'accounts', 'security', 'projects', 'money', 'system'];

const readCategory = (): FeedCategory => {
  const value = localStorage.getItem(FILTER_KEY);
  return FEED_CATEGORIES.includes(value as FeedCategory) ? (value as FeedCategory) : 'all';
};

const readScope = (): WorkspaceScope => {
  const value = localStorage.getItem(SCOPE_KEY);
  return value === 'in_progress' || value === 'attention' || value === 'recent' || value === 'pending' ? value : 'pending';
};

export function WorkspacePage() {
  const { t } = useTranslation('cabinet');
  const { t: tRbac } = useTranslation('rbac');
  const { toast } = useToast();
  const { user } = useAuth();
  const services = useApiServices();
  const { openMerchantSettings } = useMerchantSettings();
  const [params, setParams] = useSearchParams();
  const isAdmin = user?.isAdmin === true || user?.role === 'ADMIN';
  const canQueue = hasPermission(user, 'withdrawal.read_any');
  const canFeed = hasPermission(user, 'audit.read');
  const actionsLock = useRef(createActionLock());
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState<WorkspaceScope>(readScope);
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [checking, setChecking] = useState(false);
  const [approve, setApprove] = useState<WorkspaceWithdrawal | null>(null);
  const [reject, setReject] = useState<WorkspaceWithdrawal | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [emergency, setEmergency] = useState<WorkspaceWithdrawal | null>(null);
  const [comment, setComment] = useState('');
  const [category, setCategory] = useState<FeedCategory>(readCategory);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [sound, setSound] = useState(() => localStorage.getItem(SOUND_KEY) === '1');
  const [items, setItems] = useState<AdminNotification[]>([]);
  const [feedReady, setFeedReady] = useState(false);
  const [feedError, setFeedError] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [pendingNew, setPendingNew] = useState(0);
  const versionRef = useRef<string | null>(null);
  const feedRequest = useRef(0);
  const armed = useRef(false);
  const servicesRef = useRef(services);
  servicesRef.current = services;

  const openId = Number(params.get('withdrawal') || 0) || null;
  const summary = useSWR(isAdmin && canQueue ? 'workspace-summary' : null, () => services.workspace.summary(), {
    refreshInterval: 5000,
    refreshWhenHidden: true,
  });
  const list = useSWR(
    isAdmin && canQueue && summary.data
      ? ['workspace-withdrawals', scope, page, summary.data.withdrawals.version, summary.data.withdrawals.counts.pending, summary.data.withdrawals.counts.in_progress, summary.data.withdrawals.counts.attention]
      : null,
    () => services.workspace.withdrawals(scope, page),
  );
  const details = useSWR(openId ? ['workspace-withdrawal', openId] : null, () => services.workspace.details(openId!));
  const notificationsVersion = summary.data?.notifications?.version ?? null;
  const summaryReady = Boolean(summary.data);

  useEffect(() => {
    const onPointer = () => {
      armed.current = true;
    };
    window.addEventListener('pointerdown', onPointer, { once: true });
    return () => window.removeEventListener('pointerdown', onPointer);
  }, []);

  useEffect(() => {
    const pending = summary.data?.withdrawals.counts.pending ?? 0;
    const critical = summary.data?.notifications?.unreadCritical ?? 0;
    document.title = workspaceDocumentTitle(pending, critical);
  }, [summary.data]);

  useEffect(() => {
    if (!canFeed) return;
    if (summaryReady && !notificationsVersion) {
      setFeedReady(true);
      setFeedError(false);
      return;
    }
    if (!notificationsVersion || notificationsVersion === versionRef.current) return;
    const previous = versionRef.current;
    const requestId = feedRequest.current + 1;
    feedRequest.current = requestId;
    const feed = document.getElementById('workspace-feed');
    const scrolled = Boolean(feed && feed.scrollTop > 40);
    void servicesRef.current.workspace.notifications({
      changedSince: previous ?? undefined,
      category: category === 'all' ? undefined : category,
      unreadOnly,
    }).then((pageResult) => {
      if (requestId !== feedRequest.current) return;
      versionRef.current = notificationsVersion;
      setFeedReady(true);
      setFeedError(false);
      setItems((current) => {
        const merged = previous
          ? mergeNotifications(current, pageResult.items, Boolean(pageResult.truncated))
          : { items: pageResult.items, inserted: 0 };
        if (scrolled && merged.inserted > 0) setPendingNew((count) => count + merged.inserted);
        if (merged.inserted > 0 && sound && armed.current && !document.hidden) beep();
        return merged.items;
      });
      if (!previous) setCursor(pageResult.nextCursor);
    }).catch(() => {
      if (requestId !== feedRequest.current) return;
      setFeedError(true);
      setFeedReady(true);
    });
  }, [canFeed, category, notificationsVersion, sound, summaryReady, unreadOnly]);

  const open = (id: number) => {
    const next = new URLSearchParams(params);
    if (id > 0) next.set('withdrawal', String(id));
    else next.delete('withdrawal');
    setParams(next);
  };

  const run = (task: () => Promise<void>) => {
    void actionsLock.current.run(async () => {
      setBusy(true);
      try {
        await task();
      } finally {
        setBusy(false);
      }
    });
  };

  if (!isAdmin || (!canQueue && !canFeed) || summary.error instanceof ForbiddenError) {
    return <StatusBanner variant="destructive">{t('workspace.forbidden')}</StatusBanner>;
  }

  const counts = summary.data?.withdrawals.counts ?? { pending: 0, in_progress: 0, attention: 0 };

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="grid grid-cols-[auto_1fr] items-start gap-2.5 rounded-lg border border-border bg-accent/40 p-3 sm:gap-3 sm:p-4">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
        <div className="min-w-0 text-sm">
          <p className="font-medium text-foreground">{t('workspace.info.title')}</p>
          <p className="mt-0.5 text-muted-foreground">{t('workspace.info.body')}</p>
        </div>
      </div>
      {summary.error && !(summary.error instanceof ForbiddenError) ? (
        <StatusBanner variant="pending">{t('workspace.offline', { time: summary.data?.serverTime ?? '' })}</StatusBanner>
      ) : null}
      <div className="flex min-w-0 flex-col gap-4">
        <div id="workspace-queue" className="flex min-h-0 min-w-0 flex-col">
          {canQueue ? (
            <WorkspaceQueue
              scope={scope}
              counts={counts}
              rows={list.data?.data ?? []}
              page={page}
              total={list.data?.pagination.total ?? 0}
              limit={list.data?.pagination.limit ?? 50}
              serverTime={summary.data?.serverTime ?? new Date().toISOString()}
              details={details.data ?? null}
              openId={openId}
              expandedId={expandedId}
              listLoading={(!summary.data && summary.isLoading) || list.isLoading}
              listError={Boolean(list.error)}
              detailsError={Boolean(details.error)}
              checking={checking}
              comment={comment}
              busy={busy}
              canReconcile={hasPermission(user, 'withdrawal.execute')}
              onReconciled={() => {
                void list.mutate();
                void details.mutate();
                void summary.mutate();
              }}
              onScope={(next) => {
                localStorage.setItem(SCOPE_KEY, next);
                setScope(next);
                setPage(1);
              }}
              onPage={setPage}
              onOpen={open}
              onExpand={(id) => setExpandedId((current) => (current === id ? null : id))}
              onApprove={setApprove}
              onReject={setReject}
              onEmergency={setEmergency}
              onCommentChange={setComment}
              onComment={(id) => run(async () => {
                await services.workspace.addComment(id, comment.trim());
                setComment('');
                await details.mutate();
                await list.mutate();
              })}
            />
          ) : null}
        </div>
        <div className="flex min-h-0 min-w-0 flex-col">
          {canFeed ? (
            <NotificationFeed
              items={items}
              loading={!feedReady}
              error={feedError}
              category={category}
              unreadOnly={unreadOnly}
              pendingNew={pendingNew}
              unverifiedCount={summary.data?.autoWithdrawal.unverifiedCount ?? 0}
              sound={sound}
              hasMore={Boolean(cursor)}
              onCategory={(next) => {
                localStorage.setItem(FILTER_KEY, next);
                setCategory(next);
                versionRef.current = null;
              }}
              onUnreadOnly={(value) => {
                setUnreadOnly(value);
                versionRef.current = null;
              }}
              onSound={(value) => {
                localStorage.setItem(SOUND_KEY, value ? '1' : '0');
                setSound(value);
              }}
              onReadAll={() => run(async () => {
                await services.workspace.markRead();
                setItems((current) => current.map((item) => ({ ...item, isRead: true })));
                await summary.mutate();
              })}
              onLoadMore={() => run(async () => {
                if (!cursor) return;
                const pageResult = await services.workspace.notifications({
                  before: cursor,
                  category: category === 'all' ? undefined : category,
                  unreadOnly,
                });
                setItems((current) => [...current, ...pageResult.items]);
                setCursor(pageResult.nextCursor);
              })}
              onShowNew={() => {
                document.getElementById('workspace-feed')?.scrollTo({ top: 0 });
                setPendingNew(0);
              }}
              onOpen={(item) => {
                if (item.kind === 'auto_withdrawal.changed' && item.subject) {
                  const rules = Array.isArray(item.params.unverifiedRules) ? item.params.unverifiedRules : [];
                  const pair = rules[0] && typeof rules[0] === 'object' ? `${String((rules[0] as { assetCode?: string }).assetCode ?? '')}:${String((rules[0] as { networkCode?: string }).networkCode ?? '')}` : '';
                  openMerchantSettings(item.subject.id, { email: item.subject.email ?? undefined, pane: 'auto', highlight: pair });
                  return;
                }
                if (item.target?.type === 'withdrawal' && item.target.id) {
                  document.getElementById('workspace-queue')?.scrollIntoView({ block: 'start' });
                  open(Number(item.target.id));
                  return;
                }
                if (item.subject) {
                  openMerchantSettings(item.subject.id, { email: item.subject.email ?? undefined });
                  return;
                }
                window.location.assign(`/audit-log?event=${encodeURIComponent(item.auditEventId)}`);
              }}
            />
          ) : null}
        </div>
      </div>
      <AdminCommandDialog
        open={Boolean(approve)}
        title={t('workspace.approve')}
        description={approve ? `${approve.amount} ${approve.assetCode} · ${approve.address} · ${approve.merchant.email}` : undefined}
        reasonRequired={false}
        busy={busy}
        onOpenChange={(openDialog) => !openDialog && setApprove(null)}
        onConfirm={async (command) => {
          if (!approve) return;
          await actionsLock.current.run(async () => {
            setBusy(true);
            try {
              await services.withdrawals.processAdminWithdrawal(String(approve.id), { secondFactor: command.secondFactor });
              toast(t('withdrawals.actions.processSuccess'), 'success');
              setApprove(null);
              setChecking(false);
              await list.mutate();
              await details.mutate();
            } catch (error) {
              if (isUncertainMutationError(error)) {
                setApprove(null);
                setChecking(true);
                await details.mutate();
                await list.mutate();
                return;
              }
              toast(formatRbacError(error, tRbac, t('workspace.forbidden')), 'error');
            } finally {
              setBusy(false);
            }
          });
        }}
      />
      <Dialog open={Boolean(reject)} onOpenChange={(openDialog) => !openDialog && setReject(null)}>
        <DialogContent className="w-[calc(100%-2rem)] sm:max-w-md">
          <DialogHeader><DialogTitle>{t('workspace.reject')}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="workspace-reject">{t('workspace.rejectReason')}</Label>
            <Textarea id="workspace-reject" value={rejectReason} maxLength={1000} rows={4} onChange={(event) => setRejectReason(event.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setReject(null)}>{t('compliance.factor.cancel')}</Button>
            <Button
              type="button"
              variant="destructive"
              loading={busy}
              disabled={rejectReason.trim().length < 3}
              onClick={() => {
                if (!reject) return;
                run(async () => {
                  try {
                    await services.withdrawals.cancelAdminWithdrawal(String(reject.id), { comment: rejectReason.trim() });
                    setReject(null);
                    setRejectReason('');
                    await list.mutate();
                  } catch (error) {
                    toast(formatRbacError(error, tRbac, t('workspace.forbidden')), 'error');
                  }
                });
              }}
            >
              {t('workspace.reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <EmergencyGrantDialog
        withdrawal={emergency ? {
          id: String(emergency.id),
          amount: emergency.amount,
          assetCode: emergency.assetCode,
          network: emergency.network,
          address: emergency.address,
        } : null}
        busy={busy}
        onOpenChange={(openDialog) => !openDialog && setEmergency(null)}
        onCreateGrant={async (payload) => {
          try {
            await services.rbac.createEmergencyGrant({
              withdrawalId: payload.withdrawalId,
              overrides: payload.overrides,
              idempotencyKey: payload.idempotencyKey,
              ...payload.command,
            });
            await list.mutate();
            return { success: true };
          } catch (error) {
            return { success: false, error: formatRbacError(error, tRbac, t('workspace.forbidden')) };
          }
        }}
      />
    </div>
  );
}

function beep() {
  const context = new AudioContext();
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.frequency.value = 880;
  gain.gain.value = 0.04;
  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start();
  oscillator.stop(context.currentTime + 0.12);
  window.setTimeout(() => void context.close(), 300);
}
